import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import ScrollToTopBottom from './ScrollToTopBottom';
import FloatingChatButton from './FloatingChatButton';
import { hasPermission, UserRole } from '../utils/rolePermissions';
import { todoService, Todo, TodoComment } from '../services/todoService';
import TodoDetailsModal from './TodoDetailsModal';
import { recapService, DailyRecap } from '../services/recapService';
import { searchService, SearchResults, SearchResult } from '../services/searchService';
import { formatDateLocale, formatDateLocal, getTodayDate, isDateToday, isTodoDueOrReminderToday, parseDateString } from '../utils/dateUtils';
import { getClientViewMode } from '../utils/clientViewMode';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  Building2,
  CreditCard,
  FileText,
  DollarSign,
  BarChart3,
  User,
  Menu,
  X,
  LogOut,
  LogIn,
  Settings,
  Bell,
  Search,
  ChevronDown,
  Sun,
  Moon,
  Dumbbell,
  Heart,
  Zap,
  Trophy,
  Activity,
  Target,
  Flame,
  Star,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  ListTodo,
  Calendar as CalendarIcon,
  Package,
  Shield,
  MessageSquare,
  Bot,
  Loader,
  ArrowRight,
  BookOpen,
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Store scroll position and clicked button ref before navigation
  const [savedScrollPosition, setSavedScrollPosition] = useState<number | null>(null);
  const [savedSidebarScroll, setSavedSidebarScroll] = useState<number | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const clickedButtonRef = React.useRef<HTMLAnchorElement | null>(null);
  const mainContentRef = React.useRef<HTMLDivElement | null>(null);
  const sidebarNavRef = React.useRef<HTMLElement | null>(null);
  const scrollLockRef = React.useRef<boolean>(false);
  const savedBodyStylesRef = React.useRef<{ overflow: string; position: string; top: string; width: string } | null>(null);
  const sidebarScrollLockRef = React.useRef<boolean>(false);
  const desiredSidebarScrollRef = React.useRef<number | null>(null);
  // Store sidebar scroll position that persists across remounts using sessionStorage
  const SIDEBAR_SCROLL_STORAGE_KEY = 'fitness-app-sidebar-scroll';
  
  // Helper to save scroll position to sessionStorage
  const saveSidebarScroll = (scrollTop: number) => {
    if (scrollTop > 0) {
      sessionStorage.setItem(SIDEBAR_SCROLL_STORAGE_KEY, scrollTop.toString());
    }
  };
  
  // Helper to get saved scroll position from sessionStorage
  const getSavedSidebarScroll = (): number | null => {
    const saved = sessionStorage.getItem(SIDEBAR_SCROLL_STORAGE_KEY);
    return saved ? parseFloat(saved) : null;
  };
  
  // Prevent automatic scroll to top on route changes - GLOBAL PREVENTION
  useEffect(() => {
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // GLOBAL: Prevent React Router from scrolling to top on route changes
    // This intercepts ALL route changes, not just sidebar clicks
    const preventAutoScroll = () => {
      // If we have a saved scroll position, restore it
      if (savedScrollPosition !== null && scrollLockRef.current) {
        window.scrollTo(0, savedScrollPosition);
        if (mainContentRef.current) {
          mainContentRef.current.scrollTop = savedScrollPosition;
        }
      }
    };
    
    // Listen to popstate (browser back/forward) and prevent scroll
    window.addEventListener('popstate', preventAutoScroll);
    
    // Override window.scrollTo to prevent scroll-to-top during navigation
    const originalScrollTo = window.scrollTo.bind(window);
    const scrollToOverride = function(...args: any[]) {
      // If we're navigating and trying to scroll to top, prevent it
      if (scrollLockRef.current && savedScrollPosition !== null) {
        let y = 0;
        if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
          y = (args[0] as any).top || 0;
        } else if (args.length >= 2) {
          y = args[1] || 0;
        }
        
        // If trying to scroll to top (y === 0 or very small) during navigation, prevent it
        if (y === 0 || (y < 10 && savedScrollPosition > 10)) {
          originalScrollTo(0, savedScrollPosition);
          return;
        }
      }
      // Otherwise, allow normal scroll
      originalScrollTo.apply(window, args as any);
    };
    
    // Replace window.scrollTo
    (window as any).scrollTo = scrollToOverride;
    
    return () => {
      window.removeEventListener('popstate', preventAutoScroll);
      (window as any).scrollTo = originalScrollTo;
    };
  }, [savedScrollPosition]);
  
  // AGGRESSIVE SCROLL LOCK - Prevents ALL scrolling during navigation
  // This runs immediately when isNavigating becomes true
  useEffect(() => {
    if (isNavigating && savedScrollPosition !== null) {
      // Ensure lock is active
      scrollLockRef.current = true;
      
      // Save current body styles
      savedBodyStylesRef.current = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width
      };
      
      // Don't lock body scroll - just prevent auto-scroll-to-top
      // The body scroll lock interferes with sidebar functionality
      
      // Prevent scroll events on MAIN CONTENT - this is the key!
      const preventScroll = (e: Event) => {
        if (scrollLockRef.current && savedScrollPosition !== null) {
          // Prevent scroll on window (main page content)
          const currentScroll = window.scrollY;
          if (Math.abs(currentScroll - savedScrollPosition) > 1) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            window.scrollTo(0, savedScrollPosition);
            return false;
          }
          
          // Also prevent scroll on main content div if it exists
          if (mainContentRef.current) {
            const target = e.target as HTMLElement;
            if (mainContentRef.current.contains(target) || target === mainContentRef.current) {
              const currentMainScroll = mainContentRef.current.scrollTop;
              if (Math.abs(currentMainScroll - savedScrollPosition) > 1) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                mainContentRef.current.scrollTop = savedScrollPosition;
                return false;
              }
            }
          }
        }
      };
      
      // Prevent wheel events that would scroll away
      const preventScrollWheel = (e: WheelEvent) => {
        if (scrollLockRef.current) {
          const currentScroll = window.scrollY;
          // Only prevent if it would scroll significantly away
          if (Math.abs(currentScroll - savedScrollPosition) > 10) {
            e.preventDefault();
            e.stopPropagation();
            window.scrollTo(0, savedScrollPosition);
            return false;
          }
        }
      };
      
      // Allow touch events but restore position if needed
      const preventScrollTouch = (e: TouchEvent) => {
        if (scrollLockRef.current) {
          const currentScroll = window.scrollY;
          if (Math.abs(currentScroll - savedScrollPosition) > 10) {
            e.preventDefault();
            e.stopPropagation();
            window.scrollTo(0, savedScrollPosition);
            return false;
          }
        }
      };
      
      // Add event listeners with capture phase - CRITICAL for main content
      window.addEventListener('scroll', preventScroll, { passive: false, capture: true });
      document.addEventListener('scroll', preventScroll, { passive: false, capture: true });
      document.body.addEventListener('scroll', preventScroll, { passive: false, capture: true });
      
      // Add listener specifically to main content div
      if (mainContentRef.current) {
        mainContentRef.current.addEventListener('scroll', preventScroll, { passive: false, capture: true });
      }
      
      // REMOVED: Sidebar scroll protection - let sidebar scroll freely
      
      window.addEventListener('wheel', preventScrollWheel, { passive: false, capture: true });
      document.addEventListener('wheel', preventScrollWheel, { passive: false, capture: true });
      window.addEventListener('touchmove', preventScrollTouch, { passive: false, capture: true });
      document.addEventListener('touchmove', preventScrollTouch, { passive: false, capture: true });
      
      // Continuously force scroll position and button focus - VERY AGGRESSIVE
      const forceScrollInterval = setInterval(() => {
        if (scrollLockRef.current && savedScrollPosition !== null) {
          // Force window scroll - no tolerance, always restore
          const currentWindowScroll = window.scrollY;
          if (currentWindowScroll !== savedScrollPosition) {
            window.scrollTo(0, savedScrollPosition);
          }
          
          // Force main content scroll
          if (mainContentRef.current) {
            const currentMainScroll = mainContentRef.current.scrollTop;
            if (currentMainScroll !== savedScrollPosition) {
              mainContentRef.current.scrollTop = savedScrollPosition;
            }
          }
          
          // REMOVED: All sidebar scroll manipulation - let it stay where user scrolls it
        }
      }, 1); // Check every millisecond for maximum protection
      
      return () => {
        scrollLockRef.current = false;
        clearInterval(forceScrollInterval);
        
        // Remove event listeners
        window.removeEventListener('scroll', preventScroll, { capture: true });
        document.removeEventListener('scroll', preventScroll, { capture: true });
        document.body.removeEventListener('scroll', preventScroll, { capture: true });
        
        // Remove listener from main content div
        if (mainContentRef.current) {
          mainContentRef.current.removeEventListener('scroll', preventScroll, { capture: true });
        }
        
        // REMOVED: Sidebar scroll protection removal
        
        window.removeEventListener('wheel', preventScrollWheel, { capture: true });
        document.removeEventListener('wheel', preventScrollWheel, { capture: true });
        window.removeEventListener('touchmove', preventScrollTouch, { capture: true });
        document.removeEventListener('touchmove', preventScrollTouch, { capture: true });
        
        // Final scroll restoration
        window.scrollTo(0, savedScrollPosition);
      };
    }
  }, [isNavigating, savedScrollPosition]);
  
  // Global scroll prevention for all route changes
  useEffect(() => {
    // If we're navigating, prevent scroll on route change
    if (isNavigating && savedScrollPosition !== null) {
      const handleRouteChange = () => {
        window.scrollTo(0, savedScrollPosition);
        if (mainContentRef.current) {
          mainContentRef.current.scrollTop = savedScrollPosition;
        }
      };
      
      // Call immediately
      handleRouteChange();
      
      // Call after a short delay to catch any delayed scrolls
      const timeout1 = setTimeout(handleRouteChange, 0);
      const timeout2 = setTimeout(handleRouteChange, 10);
      const timeout3 = setTimeout(handleRouteChange, 50);
      
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
      };
    }
  }, [location.pathname, isNavigating, savedScrollPosition]);
  
  // Continuously restore scroll position during navigation
  useEffect(() => {
    if (isNavigating && savedScrollPosition !== null) {
      // Continuously restore scroll position to prevent any scroll
      const restoreInterval = setInterval(() => {
        // Restore window scroll
        if (Math.abs(window.scrollY - savedScrollPosition) > 1) {
          window.scrollTo(0, savedScrollPosition);
        }
        // Restore main content scroll
        if (mainContentRef.current && Math.abs(mainContentRef.current.scrollTop - savedScrollPosition) > 1) {
          mainContentRef.current.scrollTop = savedScrollPosition;
        }
        // REMOVED: All sidebar scroll manipulation - let it stay where user scrolls it
      }, 16); // ~60fps
      
      return () => {
        clearInterval(restoreInterval);
      };
    }
  }, [isNavigating, savedScrollPosition]);
  
  // DEBUG: Intercept scrollIntoView globally to catch any calls
  useEffect(() => {
    const originalScrollIntoView = HTMLElement.prototype.scrollIntoView;
    HTMLElement.prototype.scrollIntoView = function(arg?: boolean | ScrollIntoViewOptions) {
      // Check if this is being called on a sidebar element
      if (sidebarNavRef.current && (sidebarNavRef.current.contains(this) || this === sidebarNavRef.current)) {
        return;
      }
      // For all other elements, allow normal behavior
      return originalScrollIntoView.call(this, arg);
    };
    
    return () => {
      HTMLElement.prototype.scrollIntoView = originalScrollIntoView;
    };
  }, []);
  
  // CRITICAL FIX: Restore sidebar scroll position when element is recreated
  useEffect(() => {
    if (sidebarNavRef.current) {
      const sidebar = sidebarNavRef.current;
      const sidebarId = Math.random().toString(36).substr(2, 9);
      const savedScroll = getSavedSidebarScroll();

      if (savedScroll !== null && savedScroll > 0 && sidebar.scrollTop === 0) {
      const restoreScroll = () => {
          if (sidebarNavRef.current) {
            sidebarNavRef.current.scrollTop = savedScroll;
          }
      };
      
        // Immediate restore
        restoreScroll();
        // Restore after a frame
        requestAnimationFrame(restoreScroll);
        // Restore after a small delay
        setTimeout(restoreScroll, 10);
        setTimeout(restoreScroll, 50);
        setTimeout(restoreScroll, 100);
      }
      
      // Track sidebar scroll events and save position to sessionStorage
      const handleSidebarScroll = () => {
        const currentScroll = sidebar.scrollTop;
        if (currentScroll > 0) {
          saveSidebarScroll(currentScroll);
        }
      };
      sidebar.addEventListener('scroll', handleSidebarScroll, { passive: true });
      
      
      // Monitor scrollTop property changes with getter/setter override
      let lastScrollTop = sidebar.scrollTop;
      const originalScrollTopDescriptor = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'scrollTop');
      
      if (originalScrollTopDescriptor && originalScrollTopDescriptor.set) {
        Object.defineProperty(sidebar, 'scrollTop', {
          get: originalScrollTopDescriptor.get,
          set: function(value: number) {
            lastScrollTop = value;
            if (originalScrollTopDescriptor.set) {
              originalScrollTopDescriptor.set.call(this, value);
            }
          },
          configurable: true
        });
      }
      
      // Save scroll position continuously so it survives remounts
      const scrollCheckInterval = setInterval(() => {
        const currentScrollTop = sidebar.scrollTop;
        // Always save current scroll position to sessionStorage (survives remounts)
        if (currentScrollTop > 0) {
          saveSidebarScroll(currentScrollTop);
        }
        if (currentScrollTop !== lastScrollTop && lastScrollTop !== undefined) {
          lastScrollTop = currentScrollTop;
        }
      }, 100);
      
      // Check if sidebar element is being recreated
      const checkElement = () => {
        // Monitor sidebar element presence (no logging)
      };
      const elementCheckInterval = setInterval(checkElement, 500);
      
      return () => {
        if (sidebar && sidebar.scrollTop > 0) {
          saveSidebarScroll(sidebar.scrollTop);
        }
        sidebar.removeEventListener('scroll', handleSidebarScroll);
        clearInterval(scrollCheckInterval);
        clearInterval(elementCheckInterval);
        if (originalScrollTopDescriptor) {
          try {
            Object.defineProperty(sidebar, 'scrollTop', originalScrollTopDescriptor);
          } catch {
            // Ignore restore errors
          }
        }
      };
    }
  }, []);
  
  useEffect(() => {
    // Route change (sidebar scroll preserved via sessionStorage)
  }, [location.pathname]);
  
  // Get active search from URL to show as chip
  const activeSearchTerm = searchParams.get('search') || '';
  
  // Clear active search from URL - this will trigger all pages to show all data
  const handleClearActiveSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    newParams.delete('id');
    setSearchParams(newParams, { replace: true });
  };

  const canAccessPage = (userRole: string | undefined, permissions: any, pageName: string): boolean => {
    if (userRole?.toLowerCase() === 'admin') {
      return true;
    }
    if (!permissions) {
      return false;
    }
    if (!permissions.pagePermissions || !Array.isArray(permissions.pagePermissions)) {
      return false;
    }
    const pagePerm = permissions.pagePermissions.find((p: any) => p.page === pageName);
    if (!pagePerm) {
      return false;
    }
    return pagePerm.can_view === 1;
  };
  
  // Initialize states - isRTL must be initialized based on current language
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isRTL, setIsRTL] = useState(() => i18n.language === 'ar');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [urgentTodos, setUrgentTodos] = useState<Todo[]>([]);
  const [todayRecaps, setTodayRecaps] = useState<DailyRecap[]>([]);
  const [reminderTodos, setReminderTodos] = useState<Todo[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [playedSounds, setPlayedSounds] = useState<Set<number>>(new Set());
  const [showReminderCard, setShowReminderCard] = useState(false);
  const [currentReminderIndex, setCurrentReminderIndex] = useState(0);
  const [showReminderDetailsModal, setShowReminderDetailsModal] = useState(false);
  const [selectedReminderTodo, setSelectedReminderTodo] = useState<Todo | null>(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const userRole = (user?.role?.toLowerCase() as UserRole) || 'employee';
  const isAdmin = userRole === 'admin';

  const isTodoVisibleToCurrentUser = (todo: Todo) => {
    if (isAdmin || !user?.id) return isAdmin;
    const isTeamWide =
      (todo.assign_to_all === 1 || todo.assign_to_all === true) &&
      (todo.assigned_to === null || todo.assigned_to === undefined);
    return Number(todo.assigned_to) === Number(user.id) || isTeamWide;
  };

  // Check if push notifications are enabled (which hides reminders for admin)
  const SETTINGS_STORAGE_KEY = 'fitness-app-push-notifications';
  const getPushNotificationsEnabled = (): boolean => {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
    return stored === 'true';
  };

  // Update RTL state when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      const rtl = i18n.language === 'ar';
      setIsRTL(rtl);
      if (rtl) {
        document.documentElement.setAttribute('dir', 'rtl');
        document.documentElement.setAttribute('lang', 'ar');
      } else {
        document.documentElement.setAttribute('dir', 'ltr');
        document.documentElement.setAttribute('lang', i18n.language);
      }
    };

    // Set initial state
    handleLanguageChange();

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    // Cleanup
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false); // Close mobile sidebar on resize to desktop
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial value

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on route change (mobile) and when clicking overlay
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);

  // Dark mode toggle
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Sidebar collapse toggle
  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Play notification sound - Loud and modern
  const playNotificationSound = () => {
    try {
      // Create a modern, loud notification sound
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const compressor = audioContext.createDynamicsCompressor();
      
      oscillator.connect(compressor);
      compressor.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Modern notification: higher pitch, square wave for attention
      oscillator.frequency.value = 1000; // Higher frequency
      oscillator.type = 'square'; // Square wave is more attention-grabbing
      
      // Louder volume (0.5 instead of 0.3)
      gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      // Longer duration for better noticeability
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch {
      // Ignore sound errors
    }
  };

  // Show browser notification if permission granted
  const showBrowserNotification = (todo: Todo) => {
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        const isDueToday = isDateToday(todo.due_date);
        const notificationTitle = isDueToday ? t('todo.dueDate', 'Due Date') : t('todo.reminder', 'Reminder');
        const notificationBody = isDueToday
          ? t('notifications.dueBody', { title: todo.title })
          : t('notifications.reminderBody', { title: todo.title });
        const notification = new Notification(notificationTitle, {
          body: notificationBody,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: `todo-alert-${todo.id}`,
          requireInteraction: false,
          silent: false
        });
        notification.onclick = () => {
          window.focus();
          const userRole = user?.role;
          if (userRole === 'admin') {
            navigate('/todos');
          } else {
            navigate('/my-todos');
          }
        };
      }
    } catch {
      // Ignore notification errors
    }
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        await Notification.requestPermission();
      }
    } catch {
      // Ignore permission errors
    }
  };

      // Fetch notifications (for all users, but different data)
  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      
      const today = getTodayDate();
      
      const todosResponse = await todoService.getTodos(1, 500);
      const visibleTodos = isAdmin
        ? todosResponse.data
        : todosResponse.data.filter(isTodoVisibleToCurrentUser);
      
      const urgentHighTodos = visibleTodos.filter(todo => 
        (todo.priority === 'urgent' || todo.priority === 'high') && 
        (todo.status === 'pending' || todo.status === 'in_progress')
      );
      
      // Filter todos with due date and/or reminder date matching today
      // Display notifications all day until task is completed
      const pushNotificationsEnabled = isAdmin && getPushNotificationsEnabled();
      const reminderTodosWithTodayDate = visibleTodos.filter(todo => {
        if (todo.status === 'completed' || todo.status === 'cancelled') {
          return false;
        }
        if (!isTodoDueOrReminderToday(todo)) {
          return false;
        }
        if (pushNotificationsEnabled) {
          if (!playedSounds.has(todo.id)) {
            showBrowserNotification(todo);
            setPlayedSounds(prev => new Set(prev).add(todo.id));
          }
          return false;
        }
        return true;
      });

      // Fetch today's recaps (only for admin)
      const recapsResult = userRole === 'admin' ? await recapService.getAllRecaps(today, today) : { data: [], pagination: { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 } };
      const recaps = recapsResult.data || [];
      
      setUrgentTodos(urgentHighTodos);
      setTodayRecaps(recaps);
      
      setReminderTodos(reminderTodosWithTodayDate);
      if (reminderTodosWithTodayDate.length > 0 && !notificationsOpen) {
        setShowReminderCard(true);
        setCurrentReminderIndex(0);
      } else if (reminderTodosWithTodayDate.length === 0) {
        setShowReminderCard(false);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const openReminderTaskModal = async (todo: Todo) => {
    setSelectedReminderTodo(todo);
    setShowReminderDetailsModal(true);
    setShowReminderCard(false);
    setNotificationsOpen(false);
    try {
      const fullTodo = await todoService.getTodoById(todo.id);
      setSelectedReminderTodo(fullTodo);
    } catch (error) {
      console.error('Failed to load reminder task details:', error);
    }
  };

  const handleReminderStatusChange = async (todo: Todo, newStatus: string) => {
    const previousStatus = todo.status;
    const updatedTodo = { ...todo, status: newStatus as Todo['status'] };
    setSelectedReminderTodo(updatedTodo);
    setReminderTodos((prev) =>
      prev.map((t) => (t.id === todo.id ? updatedTodo : t)).filter((t) => t.status !== 'completed' && t.status !== 'cancelled')
    );
    try {
      await todoService.updateTodo(todo.id, { status: newStatus as Todo['status'] });
      if (newStatus === 'completed' || newStatus === 'cancelled') {
        setShowReminderDetailsModal(false);
        setSelectedReminderTodo(null);
        fetchNotifications();
      }
    } catch (error) {
      console.error('Failed to update reminder task status:', error);
      setSelectedReminderTodo({ ...todo, status: previousStatus });
    }
  };

  const handleReminderAddComment = async (todoId: number, comment: string): Promise<TodoComment | undefined> => {
    try {
      const newComment = await todoService.addComment(todoId, comment);
      setSelectedReminderTodo((prev) =>
        prev && prev.id === todoId
          ? { ...prev, comments: [newComment, ...(prev.comments || [])] }
          : prev
      );
      return newComment;
    } catch (error) {
      console.error('Failed to add comment on reminder task:', error);
      throw error;
    }
  };

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (notificationsOpen) {
      fetchNotifications();
    }
  }, [notificationsOpen]);

  useEffect(() => {
    requestNotificationPermission();
  }, [user, userRole]);

  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, userRole]);

  useEffect(() => {
    const handleRefreshNotifications = () => {
      fetchNotifications();
    };

    window.addEventListener('refreshNotifications', handleRefreshNotifications);
    return () => {
      window.removeEventListener('refreshNotifications', handleRefreshNotifications);
    };
  }, []);

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (notificationsOpen && !target.closest('.notifications-container')) {
        setNotificationsOpen(false);
      }
    };

    if (notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [notificationsOpen]);

  // Handle search with debounce (primary scope = faster parallel client-focused search)
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (!searchQuery.trim()) {
      setSearchResults(null);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);

    const abortController = new AbortController();

    const timeout = setTimeout(async () => {
      try {
        const response = await searchService.globalSearch(searchQuery.trim(), {
          scope: 'primary',
          signal: abortController.signal
        });
        if (!abortController.signal.aborted) {
          setSearchResults(response.data);
          setIsSearching(false);
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        if (!abortController.signal.aborted) {
          setSearchResults(null);
          setIsSearching(false);
        }
      }
    }, 250);

    setSearchTimeout(timeout);

    return () => {
      clearTimeout(timeout);
      abortController.abort();
    };
  }, [searchQuery]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showSearchResults && !target.closest('.search-container')) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSearchResults]);

  // Handle navigation from search results
  const handleSearchResultClick = (result: SearchResult) => {
    const originalQuery = searchQuery; // Save the original search query
    setShowSearchResults(false);
    setSearchQuery('');
    
    // Build search query from the result's relevant fields
    let searchValue = '';
    
    // For clients, use first_name and last_name directly
    if (result.type === 'client') {
      const firstName = (result.first_name || '').trim();
      const lastName = (result.last_name || '').trim();
      if (firstName && lastName) {
        searchValue = `${firstName} ${lastName}`;
      } else if (firstName) {
        searchValue = firstName;
      } else if (lastName) {
        searchValue = lastName;
      } else if (result.display_name) {
        searchValue = result.display_name.trim();
      }
    } 
    // For employees, use first_name and last_name
    else if (result.type === 'employee') {
      const firstName = (result.first_name || '').trim();
      const lastName = (result.last_name || '').trim();
      if (firstName && lastName) {
        searchValue = `${firstName} ${lastName}`;
      } else if (firstName) {
        searchValue = firstName;
      } else if (lastName) {
        searchValue = lastName;
      } else if (result.display_name) {
        searchValue = result.display_name.trim();
      }
    }
    // For invoices, use invoice_number or client name
    else if (result.type === 'invoice') {
      if (result.invoice_number) {
        searchValue = result.invoice_number;
      } else if (result.client_first_name && result.client_last_name) {
        searchValue = `${result.client_first_name} ${result.client_last_name}`;
      } else if (result.client_first_name) {
        searchValue = result.client_first_name;
      } else if (result.client_last_name) {
        searchValue = result.client_last_name;
      } else if (result.display_name) {
        // Extract client name from display_name like "INV-123 - John Doe"
        const match = result.display_name.match(/\s*-\s*(.+)$/);
        searchValue = match ? match[1].trim() : result.display_name.replace(/^INV-/i, '').trim();
      }
    }
    // For payments, use client name (extract from fields or display_name)
    else if (result.type === 'payment') {
      const clientFirstName = (result.client_first_name || '').trim();
      const clientLastName = (result.client_last_name || '').trim();
      if (clientFirstName && clientLastName) {
        searchValue = `${clientFirstName} ${clientLastName}`;
      } else if (clientFirstName) {
        searchValue = clientFirstName;
      } else if (clientLastName) {
        searchValue = clientLastName;
      } else if (result.display_name) {
        // Extract client name from display_name like "Payment - John Doe - 1000 agence_cash"
        // Try to extract the name part between "Payment -" and the last " -"
        let cleaned = result.display_name.replace(/^Payment\s*-\s*/i, '');
        // Remove everything after the last " - " (amount and payment method)
        const lastDashIndex = cleaned.lastIndexOf(' - ');
        if (lastDashIndex > 0) {
          cleaned = cleaned.substring(0, lastDashIndex).trim();
        }
        searchValue = cleaned || result.display_name.trim();
      }
    }
    // For subscriptions, use client name (extract from fields or display_name)
    else if (result.type === 'subscription') {
      const clientFirstName = (result.client_first_name || '').trim();
      const clientLastName = (result.client_last_name || '').trim();
      if (clientFirstName && clientLastName) {
        searchValue = `${clientFirstName} ${clientLastName}`;
      } else if (clientFirstName) {
        searchValue = clientFirstName;
      } else if (clientLastName) {
        searchValue = clientLastName;
      } else if (result.display_name) {
        // Extract client name from display_name like "John Doe - Subscription Type"
        // Try to extract the name part before " - "
        const dashIndex = result.display_name.indexOf(' - ');
        if (dashIndex > 0) {
          searchValue = result.display_name.substring(0, dashIndex).trim();
        } else {
          searchValue = result.display_name.trim();
        }
      }
    }
    // For check-ins, use client name
    else if (result.type === 'checkin') {
      if (result.client_first_name && result.client_last_name) {
        searchValue = `${result.client_first_name} ${result.client_last_name}`;
      } else if (result.client_first_name) {
        searchValue = result.client_first_name;
      } else if (result.client_last_name) {
        searchValue = result.client_last_name;
      } else if (result.display_name) {
        searchValue = result.display_name.trim();
      }
    }
    // For todos, use title
    else if (result.type === 'todo') {
      if (result.title) {
        searchValue = result.title;
      } else if (result.display_name) {
        searchValue = result.display_name.trim();
      }
    }
    // For recaps, use employee name or date
    else if (result.type === 'recap') {
      const empFirstName = (result.employee_first_name || '').trim();
      const empLastName = (result.employee_last_name || '').trim();
      if (empFirstName && empLastName) {
        searchValue = `${empFirstName} ${empLastName}`;
      } else if (empFirstName) {
        searchValue = empFirstName;
      } else if (empLastName) {
        searchValue = empLastName;
      } else if (result.recap_date) {
        searchValue = String(result.recap_date).trim();
      } else if (result.display_name) {
        searchValue = result.display_name.trim();
      }
    }
    // For audit logs, use action or entity type
    else if (result.type === 'auditlog') {
      if (result.action) {
        searchValue = result.action;
      } else if (result.entity_type) {
        searchValue = result.entity_type;
      } else if (result.display_name) {
        searchValue = result.display_name.trim();
      }
    }
    // For stores and packs, use name
    else if (result.name) {
      searchValue = result.name;
    }
    // Final fallback to display_name (already cleaned in specific handlers above)
    else if (result.display_name) {
      searchValue = result.display_name.trim();
    }
    
    // If we have an ID, we can pass both search and id for better filtering
    const params = new URLSearchParams();
    if (searchValue.trim()) {
      params.set('search', searchValue.trim());
    }
    if (result.id) {
      params.set('id', result.id.toString());
    }
    
    const queryString = params.toString();
    
    switch (result.type) {
      case 'client':
        params.set('viewMode', getClientViewMode(result));
        navigate(`/clients?${params.toString()}`);
        break;
      case 'employee':
        if (userRole === 'admin') {
          navigate(`/employees${queryString ? `?${queryString}` : ''}`);
        }
        break;
      case 'subscription':
        // Add tab based on subscription status
        if (result.status) {
          let tab = 'active'; // default
          const status = String(result.status).toLowerCase();
          if (status === 'suspended') {
            tab = 'paused';
          } else if (status === 'cancelled') {
            tab = 'cancelled';
          } else if (status === 'completed' || status === 'expired') {
            tab = 'completed';
          } else if (status === 'active') {
            tab = 'active';
          }
          params.set('tab', tab);
        }
        navigate(`/subscriptions?${params.toString()}`);
        break;
      case 'invoice':
        navigate(`/invoices${queryString ? `?${queryString}` : ''}`);
        break;
      case 'payment':
        navigate(`/payments${queryString ? `?${queryString}` : ''}`);
        break;
      case 'store':
        if (userRole === 'admin') {
          navigate(`/stores${queryString ? `?${queryString}` : ''}`);
        }
        break;
      case 'todo':
        if (userRole === 'admin') {
          navigate(`/todos${queryString ? `?${queryString}` : ''}`);
        } else {
          navigate(`/my-todos${queryString ? `?${queryString}` : ''}`);
        }
        break;
      case 'checkin':
        navigate(`/checkins${queryString ? `?${queryString}` : ''}`);
        break;
      case 'pack':
        navigate(`/packs${queryString ? `?${queryString}` : ''}`);
        break;
      case 'recap':
        navigate(`/recaps${queryString ? `?${queryString}` : ''}`);
        break;
      case 'auditlog':
        if (userRole === 'admin') {
          navigate(`/audit-logs${queryString ? `?${queryString}` : ''}`);
        }
        break;
      default:
        break;
    }
  };

  // Get result icon and color based on type
  const getResultIcon = (type: string) => {
    switch (type) {
      case 'client':
        return <Heart className="h-4 w-4" />;
      case 'employee':
        return <UserCheck className="h-4 w-4" />;
      case 'subscription':
        return <Dumbbell className="h-4 w-4" />;
      case 'invoice':
        return <FileText className="h-4 w-4" />;
      case 'payment':
        return <DollarSign className="h-4 w-4" />;
      case 'store':
        return <Building2 className="h-4 w-4" />;
      case 'todo':
        return <ListTodo className="h-4 w-4" />;
      case 'checkin':
        return <LogIn className="h-4 w-4" />;
      case 'pack':
        return <Package className="h-4 w-4" />;
      case 'recap':
        return <Activity className="h-4 w-4" />;
      case 'auditlog':
        return <Shield className="h-4 w-4" />;
      default:
        return <Search className="h-4 w-4" />;
    }
  };

  const getResultColor = (type: string) => {
    switch (type) {
      case 'client':
        return 'text-pink-500';
      case 'employee':
        return 'text-green-500';
      case 'subscription':
        return 'text-purple-500';
      case 'invoice':
        return 'text-indigo-500';
      case 'payment':
        return 'text-emerald-500';
      case 'store':
        return 'text-orange-500';
      case 'todo':
        return 'text-violet-500';
      case 'checkin':
        return 'text-teal-500';
      case 'pack':
        return 'text-red-500';
      case 'recap':
        return 'text-amber-500';
      case 'auditlog':
        return 'text-slate-500';
      default:
        return 'text-gray-500';
    }
  };
  
  const allNavigationItems = [
    // Core
    { name: t('navigation.dashboard'), href: '/dashboard', icon: LayoutDashboard, badge: null, color: 'from-teal-500 to-emerald-600', permission: 'dashboard', section: 'Core' },
    
    // Management
    { name: t('navigation.clients'), href: '/clients', icon: Heart, badge: null, color: 'from-teal-500 to-emerald-600', permission: 'clients', section: 'Management' },
    { name: t('navigation.checkins'), href: '/checkins', icon: LogIn, badge: null, color: 'from-emerald-500 to-green-600', permission: 'checkins', section: 'Management' },
    { name: t('navigation.employees'), href: '/employees', icon: UserCheck, badge: null, color: 'from-green-500 to-teal-600', permission: 'admin_only', section: 'Management' },
    { name: t('navigation.stores'), href: '/stores', icon: Building2, badge: null, color: 'from-teal-500 to-emerald-600', permission: 'admin_only', section: 'Management' },
    
    // Business
    { name: t('navigation.subscriptions'), href: '/subscriptions', icon: Dumbbell, badge: null, color: 'from-teal-500 to-emerald-600', permission: 'subscriptions', section: 'Business' },
    { name: t('navigation.packs'), href: '/packs', icon: Package, badge: null, color: 'from-teal-500 to-emerald-600', permission: 'packs', section: 'Business' },
    { name: t('navigation.invoices'), href: '/invoices', icon: FileText, badge: null, color: 'from-teal-500 to-cyan-600', permission: 'invoices', section: 'Business' },
    { name: t('navigation.payments'), href: '/payments', icon: DollarSign, badge: null, color: 'from-emerald-500 to-green-600', permission: 'payments', section: 'Business' },
    { name: t('navigation.paymentAlerts') || 'Payment Alerts', href: '/payment-alerts', icon: Bell, badge: null, color: 'from-teal-500 to-amber-600', permission: 'payments', section: 'Business' },
    
    // Analytics
    { name: t('navigation.reports'), href: '/reports', icon: BarChart3, badge: null, color: 'from-teal-500 to-emerald-600', permission: 'reports', section: 'Analytics' },
    { name: t('navigation.dailyRecaps'), href: '/recaps', icon: Activity, badge: null, color: 'from-teal-500 to-emerald-600', permission: 'recaps', section: 'Analytics' },
    { name: t('navigation.auditLogs'), href: '/audit-logs', icon: Shield, badge: null, color: 'from-amber-500 to-orange-600', permission: 'admin_only', section: 'Analytics' },
    { name: t('navigation.verificationCenter', 'Verification Center'), href: '/verification-center', icon: Shield, badge: null, color: 'from-teal-500 to-emerald-600', permission: 'admin_only', section: 'Analytics' },
    
    // Tasks
    { name: t('navigation.todoManagement'), href: '/todos', icon: ListTodo, badge: null, color: 'from-teal-500 to-emerald-600', permission: 'admin_only', section: 'Tasks' },
    { name: t('navigation.myTasks'), href: '/my-todos', icon: CheckCircle, badge: null, color: 'from-green-500 to-emerald-600', permission: null, section: 'Tasks' },
    { name: t('navigation.taskCalendar'), href: '/admin-calendar', icon: CalendarIcon, badge: null, color: 'from-teal-500 to-emerald-600', permission: 'admin_only', section: 'Tasks' },
    { name: t('navigation.myCalendar'), href: '/my-calendar', icon: CalendarIcon, badge: null, color: 'from-teal-500 to-emerald-600', permission: null, section: 'Tasks' },
    // Support
    { name: t('navigation.chatbot', 'Assistant'), href: '/chatbot', icon: MessageSquare, badge: null, color: 'from-teal-500 to-emerald-600', permission: null, section: 'Support' },
    { name: t('navigation.digests', 'Digest TGG'), href: '/digests', icon: Bell, badge: null, color: 'from-emerald-500 to-teal-600', permission: null, section: 'Support' },
    { name: t('navigation.relanceExcel', 'Relance Excel'), href: '/relance-excel', icon: FileText, badge: null, color: 'from-emerald-500 to-teal-700', permission: 'admin_only', section: 'Support' },
    { name: t('navigation.agent', 'Agent IA'), href: '/agent', icon: Bot, badge: null, color: 'from-violet-500 to-purple-600', permission: 'admin_only', section: 'Support' },
    { name: t('navigation.apiDocs', 'API reference'), href: '/api-docs', icon: BookOpen, badge: null, color: 'from-slate-600 to-slate-800', permission: 'admin_only', section: 'Support' },
  ];

  const navigation = allNavigationItems.filter(item => {
    const userRole = user?.role?.toLowerCase();
    const isAdmin = userRole === 'admin';
    
    // Remove "My Tasks" and "Task Calendar" for admin users
    if (isAdmin) {
      if (item.href === '/my-todos' || item.href === '/admin-calendar') {
        return false;
      }
    }
    
    // Handle admin_only permissions - STRICT CHECK (Audit Logs, etc.)
    if (item.permission === 'admin_only') {
      return isAdmin;
    }
    
    // Dashboard should always be visible to all authenticated users
    if (item.permission === 'dashboard') {
      return true; // Always show dashboard to all users
    }
    
    // Special case: Recaps should be visible to all employees (they need to create their own recaps)
    if (item.permission === 'recaps') {
      return true; // Always show recaps to all users
    }
    
    // For pages with permissions, check page-level permissions
    if (item.permission) {
      const pageMap: any = {
        'clients': 'clients',
        'checkins': 'checkins',
        'employees': 'employees',
        'stores': 'stores',
        'subscriptions': 'subscriptions',
        'packs': 'packs',
        'invoices': 'invoices',
        'payments': 'payments',
        'reports': 'reports',
        'recaps': 'recaps',
      };
      
      const pageName = pageMap[item.permission];
      if (pageName) {
        return canAccessPage(user?.role, user?.permissions, pageName);
      }
    }
    
    if (item.permission && !isAdmin) {
      return false;
    }
    if (!item.permission) {
      return true;
    }
    return false;
  });

  const isCurrentPath = (path: string) => {
    return location.pathname === path;
  };

  const getPageTitle = () => {
    if (location.pathname === '/api-docs') {
      return t('navigation.apiDocs', 'API reference');
    }
    const currentNav = navigation.find(item => isCurrentPath(item.href));
    return currentNav?.name || t('navigation.dashboard');
  };

  return (
    <div className={`min-h-screen font-poppins flex ${isDarkMode ? 'dark bg-gradient-to-br from-gray-900 via-slate-900 to-teal-900/30' : 'bg-gradient-to-br from-teal-50/80 via-slate-50 to-teal-50/60'}`}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
        </div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 bottom-0 ${isRTL ? 'right-0' : 'left-0'} z-50 transform transition-all duration-500 ease-in-out ${
          isMobile 
            ? (sidebarOpen ? 'translate-x-0' : (isRTL ? 'translate-x-full' : '-translate-x-full'))
            : 'lg:translate-x-0'
        } bg-white shadow-2xl ${isRTL ? 'border-l' : 'border-r'} border-border-light flex flex-col w-72 sm:w-80 lg:w-72`}
        style={{
          width: isMobile 
            ? (sidebarOpen ? (window.innerWidth >= 640 ? '320px' : '288px') : '0px')
            : (sidebarCollapsed ? '80px' : '288px'),
          transition: 'width 500ms ease-in-out, transform 500ms ease-in-out',
          flexShrink: 0,
          height: '100vh',
          maxHeight: '100vh',
          position: 'fixed',
          top: 0,
          overflow: isMobile && !sidebarOpen ? 'hidden' : 'visible'
        }}
        aria-label="Main navigation"
      >
        {/* Logo and toggle button */}
        <div className={`flex items-center relative ${sidebarCollapsed ? 'justify-center px-2 sm:px-3' : 'justify-between px-4 sm:px-6'} h-16 sm:h-20 border-b transition-all duration-500 ease-in-out ${
          isDarkMode ? 'border-gray-700/50' : 'border-white/20'
        }`}>
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''} ${isRTL ? 'space-x-reverse' : ''} ${sidebarCollapsed ? '' : 'space-x-2 sm:space-x-3'}`}>
            <div className="relative flex-shrink-0">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl sm:rounded-2xl bg-black p-1.5 shadow-lg">
                <img 
                  src="/tg.png" 
                  alt="TEAMGREEKGODS Logo" 
                  className="h-full w-full rounded-lg object-contain"
                />
              </div>
            </div>
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
              sidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
            }`}>
              <div>
                <span className={`text-sm sm:text-base lg:text-lg font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent whitespace-nowrap block`}>
                  TEAMGREEKGODS
                </span>
                <p className="text-[9px] sm:text-[10px] text-gray-500 font-medium whitespace-nowrap">{t('common.appName')}</p>
              </div>
            </div>
          </div>
          {!sidebarCollapsed && (
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
              <button
                onClick={toggleSidebarCollapse}
                className="hidden lg:block p-2 rounded-card text-text-secondary hover:text-brand-primary-blue hover:bg-brand-primary-blue/10 transition-all duration-200"
                title={t('common.collapseSidebar')}
              >
                {isRTL ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
              </button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 rounded-card text-text-secondary hover:text-brand-primary-blue hover:bg-brand-primary-blue/10 transition-all duration-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          )}
          {sidebarCollapsed && (
            <button
              onClick={toggleSidebarCollapse}
              className="hidden lg:block absolute right-2 p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
              title={t('common.expandSidebar')}
            >
              {isRTL ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav 
          ref={sidebarNavRef as React.RefObject<HTMLElement>}
          className={`mt-4 sm:mt-6 transition-all duration-500 ease-in-out ${sidebarCollapsed ? 'px-2' : 'px-3 sm:px-4'} flex-1 overflow-y-auto pb-20 sm:pb-24`}
        >
          <div className="space-y-6">
            {[
              { key: 'Core', label: t('navigation.sections.core') || 'Core' },
              { key: 'Management', label: t('navigation.sections.management') || 'Management' },
              { key: 'Business', label: t('navigation.sections.business') || 'Business' },
              { key: 'Analytics', label: t('navigation.sections.analytics') || 'Analytics' },
              { key: 'Tasks', label: t('navigation.sections.tasks') || 'Tasks' },
              { key: 'Support', label: t('navigation.sections.support') || 'Support' }
            ].map((section) => {
              const sectionItems = navigation.filter(item => item.section === section.key);
              if (sectionItems.length === 0) return null;
              
              return (
                <div key={section.key}>
                  {!sidebarCollapsed && (
                    <div className={`px-2 sm:px-4 mb-2 sm:mb-3 flex ${isRTL ? 'flex-row-reverse' : ''} items-center text-text-secondary`}>
                      <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider">{section.label}</span>
                      <div className={`flex-1 h-px ${isRTL ? 'mr-2 sm:mr-3' : 'ml-2 sm:ml-3'} bg-border-light`}></div>
                    </div>
                  )}
                  {sidebarCollapsed && (
                    <div className={`h-px mx-2 mb-2 ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                    }`}></div>
                  )}
                  <div className="space-y-1">
                    {sectionItems.map((item, index) => {
                      const Icon = item.icon;
                      const isActive = isCurrentPath(item.href);
                      const isDisabled = (item as { disabled?: boolean }).disabled === true;
                      
                      // Simple click handler - no scroll manipulation
                      const handleNavClick = () => {
                        const savedScroll = sidebarNavRef.current?.scrollTop;
                        if (savedScroll !== undefined && savedScroll > 0) {
                          saveSidebarScroll(savedScroll);
                        }
                        if (isMobile) {
                          setSidebarOpen(false);
                        }
                      };
                      if (isDisabled) {
                        return (
                          <span
                            key={item.name}
                            className={`group relative flex ${isRTL ? 'flex-row-reverse' : ''} items-center transition-all duration-500 ease-in-out ${sidebarCollapsed ? 'px-2 sm:px-3 justify-center' : 'px-2 sm:px-3 lg:px-4'} py-2 sm:py-2.5 lg:py-3 text-xs sm:text-sm font-semibold rounded-card opacity-60 cursor-not-allowed pointer-events-none ${
                              isDarkMode ? 'text-gray-500' : 'text-gray-400'
                            }`}
                            title={sidebarCollapsed ? item.name : undefined}
                            aria-disabled="true"
                          >
                            <div className="p-1.5 sm:p-2 rounded-card bg-gray-200/50 dark:bg-gray-700/50">
                              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 dark:text-gray-500" />
                            </div>
                            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isRTL ? 'mr-2 sm:mr-3' : 'ml-2 sm:ml-3'} flex-1 ${sidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
                              <span className="whitespace-nowrap text-xs sm:text-sm">{item.name}</span>
                            </div>
                          </span>
                        );
                      }
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          preventScrollReset={true}
                          className={`group relative flex ${isRTL ? 'flex-row-reverse' : ''} items-center transition-all duration-500 ease-in-out ${sidebarCollapsed ? 'px-2 sm:px-3 justify-center' : 'px-2 sm:px-3 lg:px-4'} py-2 sm:py-2.5 lg:py-3 text-xs sm:text-sm font-semibold rounded-card hover:scale-105 ${
                            isActive
                              ? `bg-brand-primary-blue/15 text-brand-primary-blue shadow-lg`
                              : `text-text-secondary hover:bg-brand-primary-blue/10 hover:text-brand-primary-blue`
                          }`}
                          onClick={() => handleNavClick()}
                          title={sidebarCollapsed ? item.name : undefined}
                        >
                          <div className={`p-1.5 sm:p-2 rounded-card transition-all duration-500 ease-in-out ${
                            isActive 
                              ? 'bg-brand-primary-blue/20 shadow-lg' 
                              : 'bg-brand-light-bg/50 group-hover:bg-brand-primary-blue/10'
                          }`}>
                            <Icon className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-500 ease-in-out ${
                              isActive 
                                ? 'text-brand-primary-blue' 
                                : 'text-text-secondary group-hover:text-brand-primary-blue'
                            }`} />
                          </div>
                          <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isRTL ? 'mr-2 sm:mr-3' : 'ml-2 sm:ml-3'} flex-1 ${
                            sidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                          }`}>
                            <span className="whitespace-nowrap text-xs sm:text-sm">{item.name}</span>
                            {item.badge && (
                              <span className={`${isRTL ? 'mr-auto' : 'ml-auto'} bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold shadow-lg animate-pulse`}>
                                {item.badge}
                              </span>
                            )}
                          </div>
                          {isActive && !sidebarCollapsed && (
                            <div className={`absolute ${isRTL ? 'left-2' : 'right-2'} w-1 h-6 sm:h-8 bg-white/30 rounded-full transition-opacity duration-200`}></div>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 transition-all duration-500 ease-in-out" style={{ maxHeight: '90px', height: '90px' }}>
          <div className={`${sidebarCollapsed ? 'p-2' : 'p-2 sm:p-3 lg:p-4'} border-t border-border-light`}>
          <div className={`flex items-center transition-all duration-500 ease-in-out ${sidebarCollapsed ? 'justify-center' : `${isRTL ? 'space-x-reverse' : ''} space-x-2 sm:space-x-3`} p-2 sm:p-2.5 lg:p-3 rounded-card bg-brand-light-bg/50 backdrop-blur-sm border border-border-light`}>
            <div className="relative flex-shrink-0">
              <div className="h-10 w-10 sm:h-12 sm:w-12 bg-brand-primary-blue rounded-card flex items-center justify-center shadow-card">
                <User className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className={`absolute -bottom-0.5 ${isRTL ? '-left-0.5' : '-right-0.5'} w-3 h-3 sm:w-4 sm:h-4 bg-green-400 rounded-full border-2 border-white`}></div>
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-1 sm:space-x-2`}>
                  <p className={`text-xs sm:text-sm font-bold truncate text-text-primary`}>
                    {user?.first_name} {user?.last_name}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-0.5 sm:mt-1">
                  <p className={`text-[10px] sm:text-xs font-medium text-text-secondary capitalize flex items-center`}>
                    <Target className={`h-2.5 w-2.5 sm:h-3 sm:w-3 ${isRTL ? 'ml-0.5 sm:ml-1' : 'mr-0.5 sm:mr-1'} flex-shrink-0`} />
                    <span className="truncate">{user?.role}</span>
                  </p>
                  <div className={`flex items-center ${isRTL ? 'space-x-reverse mr-1 sm:mr-2' : 'ml-1 sm:ml-2'} space-x-0.5 sm:space-x-1`}>
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full animate-pulse flex-shrink-0"></div>
                    <span className="text-[10px] sm:text-xs text-gray-500">{t('common.online')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div 
        ref={mainContentRef}
        className="flex-1 min-h-screen w-full lg:w-auto"
        style={{
          marginLeft: isRTL ? 0 : (isMobile ? '0' : (sidebarCollapsed ? '80px' : '288px')),
          marginRight: isRTL ? (isMobile ? '0' : (sidebarCollapsed ? '80px' : '288px')) : 0,
          transition: 'margin 500ms ease-in-out'
        }}
        onScroll={(e) => {
          // Prevent auto-scroll-to-top on main content during navigation
          if (scrollLockRef.current && savedScrollPosition !== null) {
            const target = e.currentTarget;
            if (target.scrollTop !== savedScrollPosition && Math.abs(target.scrollTop - savedScrollPosition) > 1) {
              e.preventDefault();
              e.stopPropagation();
              target.scrollTop = savedScrollPosition;
            }
          }
        }}
      >
        {/* Top bar / Header - Fully Responsive */}
        <header 
          className={`fixed top-0 z-40 shadow-xl border-b backdrop-blur-sm ${
            isDarkMode ? 'bg-gray-800/90 border-gray-700/50' : 'bg-brand-card-bg border-border-light'
          }`}
          style={{
            width: isMobile ? '100%' : sidebarCollapsed ? 'calc(100% - 80px)' : 'calc(100% - 288px)',
            [isRTL ? 'right' : 'left']: isMobile ? '0' : sidebarCollapsed ? '80px' : '288px',
            transition: 'width 500ms ease-in-out, left 500ms ease-in-out, right 500ms ease-in-out'
          }}
          aria-label="Main header"
        >
          <div className="flex items-center justify-between h-14 sm:h-16 md:h-20 px-2 sm:px-3 md:px-4 lg:px-6 xl:px-8 gap-2">
            <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3 lg:space-x-4 flex-1 min-w-0">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-1.5 sm:p-2 rounded-lg sm:rounded-xl text-text-secondary hover:text-brand-primary-blue hover:bg-brand-light-bg transition-all duration-200 flex-shrink-0"
                aria-label="Toggle sidebar"
              >
                <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
              <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3 min-w-0 flex-1">
                <div className="p-1 sm:p-1.5 md:p-2 rounded-lg sm:rounded-xl flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0D9488 0%, #2DD4BF 50%, #5EEAD4 100%)' }}>
                  <Activity className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-white" />
                </div>
                <div className="min-w-0 flex-1 hidden xs:block">
                  <h1 className={`text-sm sm:text-base md:text-xl lg:text-2xl font-bold truncate ${
                    isDarkMode ? 'text-white' : 'text-text-primary'
                  }`}>
                    {getPageTitle()}
                  </h1>
                  <p className="text-[9px] sm:text-[10px] md:text-xs lg:text-sm text-text-secondary font-medium truncate hidden sm:block">TEAMGREEKGODS Management</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2.5 lg:gap-3 xl:gap-4 flex-shrink-0">
              {/* Search Container - Fully Responsive */}
              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2.5 search-container relative">
                {/* Active Search Chip/Tag - Beautiful modern design */}
                {activeSearchTerm && (
                  <div className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-3.5 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-teal-600/30 via-emerald-600/30 to-cyan-600/30 text-teal-200 border border-teal-500/40 backdrop-blur-sm'
                      : 'bg-gradient-to-r from-teal-50 via-emerald-50 to-cyan-50 text-teal-800 border border-teal-200/60 backdrop-blur-sm'
                  }`}>
                    <span className="flex items-center gap-1 sm:gap-1.5">
                      <Search className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 flex-shrink-0" />
                      <span className="truncate max-w-[80px] xs:max-w-[100px] sm:max-w-[120px] md:max-w-[150px] lg:max-w-[200px] xl:max-w-[250px]">
                        {activeSearchTerm}
                      </span>
                    </span>
                    <button
                      onClick={handleClearActiveSearch}
                      className={`ml-0.5 sm:ml-1 p-1 sm:p-1.5 rounded-full transition-all duration-200 flex-shrink-0 hover:scale-110 ${
                        isDarkMode
                          ? 'hover:bg-teal-500/30 text-teal-300'
                          : 'hover:bg-brand-primary-blue/20 text-brand-primary-blue'
                      }`}
                      title="Clear search"
                    >
                      <X className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4" />
                    </button>
                  </div>
                )}
                
                {/* Search Input - Modern & Responsive */}
                <div className="relative group">
                  {/* Search Icon */}
                  <div className={`absolute inset-y-0 left-0 pl-2.5 sm:pl-3 md:pl-3.5 lg:pl-4 flex items-center pointer-events-none z-10 transition-colors duration-200 ${
                    showSearchResults || searchQuery
                      ? 'text-brand-primary-blue'
                      : 'text-text-secondary group-hover:text-brand-primary-blue'
                  }`}>
                    {isSearching ? (
                      <Loader className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-4 md:w-4 lg:h-5 lg:w-5 text-brand-primary-blue animate-spin" />
                    ) : (
                      <Search className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-4 md:w-4 lg:h-5 lg:w-5" />
                    )}
                  </div>
                  
                  {/* Input Field */}
                  <input
                    type="text"
                    placeholder={t('search.placeholder') || 'Search...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => {
                      if (searchResults) {
                        setShowSearchResults(true);
                      }
                    }}
                    className={`block w-[140px] xs:w-[160px] sm:w-[180px] md:w-[220px] lg:w-[260px] xl:w-[320px] 2xl:w-[380px] pl-8 sm:pl-9 md:pl-10 lg:pl-11 pr-8 sm:pr-9 md:pr-10 py-2 sm:py-2.5 md:py-3 border-2 rounded-xl sm:rounded-2xl text-xs sm:text-sm md:text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-300 ${
                      isDarkMode 
                        ? 'bg-gray-700/60 border-gray-600/60 text-white placeholder-gray-400 backdrop-blur-md focus:border-brand-primary-blue focus:ring-brand-primary-blue/50 hover:border-gray-500' 
                        : 'bg-brand-card-bg border-border-light text-text-primary placeholder-text-secondary backdrop-blur-md focus:border-brand-primary-blue focus:ring-brand-primary-blue/30 hover:border-brand-primary-blue/50 shadow-sm hover:shadow-md'
                    }`}
                  />
                  
                  {/* Clear Button */}
                  {searchQuery && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSearchResults(null);
                        setShowSearchResults(false);
                      }}
                      className={`absolute inset-y-0 right-0 pr-2.5 sm:pr-3 md:pr-3.5 flex items-center transition-all duration-200 z-10 rounded-r-xl sm:rounded-r-2xl ${
                        isDarkMode
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600/50'
                          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100/70'
                      }`}
                    >
                      <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-4 md:w-4" />
                    </button>
                  )}
                  
                  {/* Search Results Dropdown - Enhanced Design */}
                  {showSearchResults && (searchResults || isSearching) && (
                    <div className={`absolute top-full left-0 mt-2.5 w-[calc(100vw-2rem)] sm:w-[400px] md:w-[450px] lg:w-[500px] xl:w-[600px] max-h-[calc(100vh-140px)] sm:max-h-[70vh] overflow-hidden rounded-2xl shadow-2xl border-2 z-[9999] ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-700/60 backdrop-blur-xl' 
                        : 'bg-white border-gray-200/80'
                    }`}>
                      {isSearching ? (
                        <div className="p-6 text-center">
                          <Loader className="h-6 w-6 animate-spin text-teal-500 mx-auto mb-2" />
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Searching...
                          </p>
                        </div>
                      ) : searchResults && searchResults.total > 0 ? (
                        <div className="overflow-y-auto max-h-[calc(100vh-140px)] sm:max-h-[70vh]">
                          <div className="py-2">
                            {/* Results Summary - Sticky Header */}
                            <div className={`sticky top-0 z-10 px-4 sm:px-5 py-3 sm:py-3.5 backdrop-blur-md border-b shadow-sm ${isDarkMode ? 'border-gray-700/60 bg-gray-800/98' : 'bg-brand-light-bg border-border-light'}`}>
                              <div className="flex items-center justify-between">
                                <p className={`text-xs sm:text-sm font-bold ${isDarkMode ? 'text-gray-200' : 'text-text-primary'}`}>
                                  Found {searchResults.total} result{searchResults.total !== 1 ? 's' : ''}
                                </p>
                                <button
                                  onClick={() => setShowSearchResults(false)}
                                  className={`p-1.5 rounded-lg transition-all duration-200 ${
                                    isDarkMode 
                                      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50' 
                                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                  }`}
                                  title="Close results"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            </div>

                          {/* Clients */}
                          {searchResults.clients.length > 0 && (
                            <div className="py-2">
                              <div className={`px-4 py-1 text-xs font-bold uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Clients ({searchResults.clients.length})
                              </div>
                              {searchResults.clients.map((result) => (
                                <button
                                  key={`client-${result.id}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 text-left transition-all duration-200 group ${
                                    isDarkMode 
                                      ? 'hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-700/30 border-l-2 border-transparent hover:border-pink-500/50' 
                                      : 'hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-emerald-50/30 border-l-2 border-transparent hover:border-teal-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4">
                                    <div className={`p-2 rounded-lg sm:rounded-xl transition-all duration-200 ${getResultColor('client')} group-hover:scale-110 group-hover:shadow-lg`}>
                                      {getResultIcon('client')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm sm:text-base font-semibold truncate transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'}`}>
                                        {result.display_name}
                                      </p>
                                      <p className={`text-xs sm:text-sm truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {result.email || result.phone || result.store_name || 'Client'}
                                      </p>
                                    </div>
                                    <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 ${isDarkMode ? 'text-gray-500 group-hover:text-pink-400 group-hover:translate-x-1' : 'text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1'}`} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Employees */}
                          {searchResults.employees.length > 0 && (
                            <div className="py-2">
                              <div className={`px-4 py-1 text-xs font-bold uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Employees ({searchResults.employees.length})
                              </div>
                              {searchResults.employees.map((result) => (
                                <button
                                  key={`employee-${result.id}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 text-left transition-all duration-200 group ${
                                    isDarkMode 
                                      ? 'hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-700/30 border-l-2 border-transparent hover:border-teal-500/50' 
                                      : 'hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-emerald-50/30 border-l-2 border-transparent hover:border-teal-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4">
                                    <div className={`p-2 rounded-lg sm:rounded-xl transition-all duration-200 ${getResultColor('employee')} group-hover:scale-110 group-hover:shadow-lg`}>
                                      {getResultIcon('employee')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm sm:text-base font-semibold truncate transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'}`}>
                                        {result.display_name}
                                      </p>
                                      <p className={`text-xs sm:text-sm truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {result.position} {result.store_name ? `• ${result.store_name}` : ''}
                                      </p>
                                    </div>
                                    <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 ${isDarkMode ? 'text-gray-500 group-hover:text-teal-400 group-hover:translate-x-1' : 'text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1'}`} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Subscriptions */}
                          {searchResults.subscriptions.length > 0 && (
                            <div className="py-2">
                              <div className={`px-4 py-1 text-xs font-bold uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Subscriptions ({searchResults.subscriptions.length})
                              </div>
                              {searchResults.subscriptions.map((result) => (
                                <button
                                  key={`subscription-${result.id}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 text-left transition-all duration-200 group ${
                                    isDarkMode 
                                      ? 'hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-700/30 border-l-2 border-transparent hover:border-teal-500/50' 
                                      : 'hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-emerald-50/30 border-l-2 border-transparent hover:border-teal-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4">
                                    <div className={`p-2 rounded-lg sm:rounded-xl transition-all duration-200 ${getResultColor('subscription')} group-hover:scale-110 group-hover:shadow-lg`}>
                                      {getResultIcon('subscription')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm sm:text-base font-semibold truncate transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'}`}>
                                        {result.display_name}
                                      </p>
                                      <p className={`text-xs sm:text-sm truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {result.status} • {result.subscription_type_name || 'Subscription'}
                                      </p>
                                    </div>
                                    <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 ${isDarkMode ? 'text-gray-500 group-hover:text-teal-400 group-hover:translate-x-1' : 'text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1'}`} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Invoices */}
                          {searchResults.invoices.length > 0 && (
                            <div className="py-2">
                              <div className={`px-4 py-1 text-xs font-bold uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Invoices ({searchResults.invoices.length})
                              </div>
                              {searchResults.invoices.map((result) => (
                                <button
                                  key={`invoice-${result.id}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 text-left transition-all duration-200 group ${
                                    isDarkMode 
                                      ? 'hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-700/30 border-l-2 border-transparent hover:border-teal-500/50' 
                                      : 'hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-emerald-50/30 border-l-2 border-transparent hover:border-teal-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4">
                                    <div className={`p-2 rounded-lg sm:rounded-xl transition-all duration-200 ${getResultColor('invoice')}`}>
                                      {getResultIcon('invoice')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm sm:text-base font-semibold truncate transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'}`}>
                                        {result.display_name}
                                      </p>
                                      <p className={`text-xs sm:text-sm truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {result.total_amount} • {result.status}
                                      </p>
                                    </div>
                                    <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 ${isDarkMode ? 'text-gray-500 group-hover:text-teal-400 group-hover:translate-x-1' : 'text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1'}`} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Payments */}
                          {searchResults.payments.length > 0 && (
                            <div className="py-2">
                              <div className={`px-4 py-1 text-xs font-bold uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Payments ({searchResults.payments.length})
                              </div>
                              {searchResults.payments.map((result) => (
                                <button
                                  key={`payment-${result.id}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 text-left transition-all duration-200 group ${
                                    isDarkMode 
                                      ? 'hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-700/30 border-l-2 border-transparent hover:border-teal-500/50' 
                                      : 'hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-emerald-50/30 border-l-2 border-transparent hover:border-teal-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4">
                                    <div className={`p-2 rounded-lg sm:rounded-xl transition-all duration-200 ${getResultColor('payment')}`}>
                                      {getResultIcon('payment')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm sm:text-base font-semibold truncate transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'}`}>
                                        {result.display_name}
                                      </p>
                                      <p className={`text-xs sm:text-sm truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {result.payment_date ? formatDateLocale(result.payment_date, 'en-US') : ''}
                                      </p>
                                    </div>
                                    <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 ${isDarkMode ? 'text-gray-500 group-hover:text-teal-400 group-hover:translate-x-1' : 'text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1'}`} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Stores */}
                          {searchResults.stores.length > 0 && (
                            <div className="py-2">
                              <div className={`px-4 py-1 text-xs font-bold uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Stores ({searchResults.stores.length})
                              </div>
                              {searchResults.stores.map((result) => (
                                <button
                                  key={`store-${result.id}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 text-left transition-all duration-200 group ${
                                    isDarkMode 
                                      ? 'hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-700/30 border-l-2 border-transparent hover:border-teal-500/50' 
                                      : 'hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-emerald-50/30 border-l-2 border-transparent hover:border-teal-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4">
                                    <div className={`p-2 rounded-lg sm:rounded-xl transition-all duration-200 ${getResultColor('store')}`}>
                                      {getResultIcon('store')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm sm:text-base font-semibold truncate transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'}`}>
                                        {result.display_name}
                                      </p>
                                      <p className={`text-xs sm:text-sm truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {result.address || result.phone || 'Store'}
                                      </p>
                                    </div>
                                    <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 ${isDarkMode ? 'text-gray-500 group-hover:text-teal-400 group-hover:translate-x-1' : 'text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1'}`} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Todos */}
                          {searchResults.todos.length > 0 && (
                            <div className="py-2">
                              <div className={`px-4 py-1 text-xs font-bold uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Todos ({searchResults.todos.length})
                              </div>
                              {searchResults.todos.map((result) => (
                                <button
                                  key={`todo-${result.id}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 text-left transition-all duration-200 group ${
                                    isDarkMode 
                                      ? 'hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-700/30 border-l-2 border-transparent hover:border-teal-500/50' 
                                      : 'hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-emerald-50/30 border-l-2 border-transparent hover:border-teal-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4">
                                    <div className={`p-2 rounded-lg sm:rounded-xl transition-all duration-200 ${getResultColor('todo')}`}>
                                      {getResultIcon('todo')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm sm:text-base font-semibold truncate transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'}`}>
                                        {result.display_name}
                                      </p>
                                      <p className={`text-xs sm:text-sm truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {result.priority} • {result.status}
                                      </p>
                                    </div>
                                    <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 ${isDarkMode ? 'text-gray-500 group-hover:text-teal-400 group-hover:translate-x-1' : 'text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1'}`} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Check-ins */}
                          {searchResults.checkins.length > 0 && (
                            <div className="py-2">
                              <div className={`px-4 py-1 text-xs font-bold uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Check-ins ({searchResults.checkins.length})
                              </div>
                              {searchResults.checkins.map((result) => (
                                <button
                                  key={`checkin-${result.id}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 text-left transition-all duration-200 group ${
                                    isDarkMode 
                                      ? 'hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-700/30 border-l-2 border-transparent hover:border-teal-500/50' 
                                      : 'hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-emerald-50/30 border-l-2 border-transparent hover:border-teal-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4">
                                    <div className={`p-2 rounded-lg sm:rounded-xl transition-all duration-200 ${getResultColor('checkin')}`}>
                                      {getResultIcon('checkin')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm sm:text-base font-semibold truncate transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'}`}>
                                        {result.display_name}
                                      </p>
                                      <p className={`text-xs sm:text-sm truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {result.check_in_time ? new Date(result.check_in_time).toLocaleDateString() : ''}
                                      </p>
                                    </div>
                                    <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 ${isDarkMode ? 'text-gray-500 group-hover:text-teal-400 group-hover:translate-x-1' : 'text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1'}`} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Packs */}
                          {searchResults.packs.length > 0 && (
                            <div className="py-2">
                              <div className={`px-4 py-1 text-xs font-bold uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Packs ({searchResults.packs.length})
                              </div>
                              {searchResults.packs.map((result) => (
                                <button
                                  key={`pack-${result.id}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 text-left transition-all duration-200 group ${
                                    isDarkMode 
                                      ? 'hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-700/30 border-l-2 border-transparent hover:border-teal-500/50' 
                                      : 'hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-emerald-50/30 border-l-2 border-transparent hover:border-teal-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4">
                                    <div className={`p-2 rounded-lg sm:rounded-xl transition-all duration-200 ${getResultColor('pack')}`}>
                                      {getResultIcon('pack')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm sm:text-base font-semibold truncate transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'}`}>
                                        {result.display_name}
                                      </p>
                                      <p className={`text-xs sm:text-sm truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {result.price} • {result.duration_months} months
                                      </p>
                                    </div>
                                    <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 ${isDarkMode ? 'text-gray-500 group-hover:text-teal-400 group-hover:translate-x-1' : 'text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1'}`} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Daily Recaps */}
                          {searchResults.recaps && searchResults.recaps.length > 0 && (
                            <div className="py-2">
                              <div className={`px-4 py-1 text-xs font-bold uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Daily Recaps ({searchResults.recaps.length})
                              </div>
                              {searchResults.recaps.map((result) => (
                                <button
                                  key={`recap-${result.id}`}
                                  onClick={() => handleSearchResultClick(result)}
                                  className={`w-full px-4 sm:px-5 py-3 sm:py-3.5 text-left transition-all duration-200 group ${
                                    isDarkMode 
                                      ? 'hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-700/30 border-l-2 border-transparent hover:border-teal-500/50' 
                                      : 'hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-emerald-50/30 border-l-2 border-transparent hover:border-teal-500/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 sm:gap-4">
                                    <div className={`p-2 rounded-lg sm:rounded-xl transition-all duration-200 ${getResultColor('recap')}`}>
                                      {getResultIcon('recap')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm sm:text-base font-semibold truncate transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-teal-300' : 'text-gray-900 group-hover:text-teal-600'}`}>
                                        {result.display_name}
                                      </p>
                                      <p className={`text-xs sm:text-sm truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {result.recap_date ? formatDateLocal(result.recap_date) : ''} {result.store_name ? `• ${result.store_name}` : ''}
                                      </p>
                                    </div>
                                    <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-200 ${isDarkMode ? 'text-gray-500 group-hover:text-teal-400 group-hover:translate-x-1' : 'text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1'}`} />
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                          </div>
                        </div>
                      ) : (
                        <div className="p-8 sm:p-10 text-center">
                          <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                            isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100'
                          }`}>
                            <Search className={`h-8 w-8 sm:h-10 sm:w-10 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                          </div>
                          <p className={`text-sm sm:text-base font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            No results found
                          </p>
                          <p className={`text-xs sm:text-sm mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            Try searching for &quot;{searchQuery}&quot;
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className={`p-1.5 sm:p-2 md:p-2.5 lg:p-3 rounded-lg sm:rounded-xl md:rounded-2xl transition-all duration-300 hover:scale-110 flex-shrink-0 ${
                  isDarkMode 
                    ? 'text-yellow-400 hover:bg-yellow-400/10' 
                    : 'text-gray-400 hover:bg-gray-100'
                }`}
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? <Sun className="h-4 w-4 sm:h-4.5 sm:w-4.5 md:h-5 md:w-5" /> : <Moon className="h-4 w-4 sm:h-4.5 sm:w-4.5 md:h-5 md:w-5" />}
              </button>

              {/* Notifications - Only for Admin */}
              {userRole === 'admin' && (
                <div className="relative notifications-container">
                  <button 
                    onClick={() => setNotificationsOpen(!notificationsOpen)}
                    className={`p-1.5 sm:p-2 md:p-2.5 lg:p-3 rounded-lg sm:rounded-xl md:rounded-2xl transition-all duration-300 hover:scale-110 relative flex-shrink-0 ${
                      notificationsOpen
                        ? 'bg-[#0D9488]/10 text-[#0D9488]'
                        : isDarkMode 
                        ? 'text-gray-400 hover:bg-gray-700/50 hover:text-gray-300' 
                        : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                    }`}
                    aria-label="Notifications"
                  >
                    <Bell className="h-4 w-4 sm:h-4.5 sm:w-4.5 md:h-5 md:w-5 lg:h-6 lg:w-6" />
                    {(urgentTodos.length > 0 || todayRecaps.length > 0 || reminderTodos.length > 0) && (
                      <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 h-2 w-2 sm:h-2.5 sm:w-2.5 md:h-3 md:w-3 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full animate-pulse"></span>
                    )}
                  </button>

                  {/* Notifications Dropdown */}
                  {notificationsOpen && (
                    <div 
                      className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-2 w-80 sm:w-96 rounded-2xl shadow-2xl py-2 z-50 border ${
                        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                      }`}
                      style={{ maxHeight: '600px', overflowY: 'auto' }}
                    >
                      <div className="px-4 py-3 border-b border-gray-200">
                        <h3 className="font-bold text-lg text-[#0D9488]">Notifications</h3>
                        <p className="text-xs text-gray-500 mt-1">Reminders, urgent tasks & today's recaps</p>
                      </div>

                      {loadingNotifications ? (
                        <div className="p-8 text-center">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D9488]"></div>
                          <p className="text-sm text-gray-500 mt-2">Loading...</p>
                        </div>
                      ) : (
                        <div className="py-2">
                          {/* Today's Reminders */}
                          {reminderTodos.length > 0 && (
                            <div className="mb-4">
                              <div className="px-4 py-2 bg-teal-500/10 border-b border-gray-200">
                                <h4 className="font-semibold text-sm text-teal-600 flex items-center gap-2">
                                  <Bell className="h-4 w-4" />
                                  Today's Reminders ({reminderTodos.length})
                                </h4>
                              </div>
                              <div className="max-h-64 overflow-y-auto">
                                {reminderTodos.map((todo) => (
                                  <button
                                    key={todo.id}
                                    type="button"
                                    onClick={() => openReminderTaskModal(todo)}
                                    className="w-full text-left block px-4 py-3 hover:bg-gray-50 border-b border-gray-100 transition-colors"
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className="p-1.5 rounded-lg bg-teal-100 text-teal-600 flex-shrink-0 animate-pulse">
                                        <Bell className="h-3.5 w-3.5" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm text-gray-900 truncate">{todo.title}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          {todo.assignee_first_name} {todo.assignee_last_name}
                                          {todo.reminder_date && (
                                            <span> • Reminder: {formatDateLocale(todo.reminder_date, 'en-US')}</span>
                                          )}
                                        </p>
                                        {todo.due_date && (
                                          <p className="text-xs text-gray-400 mt-1">
                                            Due: {formatDateLocale(todo.due_date, 'en-US')}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Urgent/High Priority Todos */}
                          {urgentTodos.length > 0 && (
                            <div className="mb-4">
                              <div className="px-4 py-2 bg-teal-500/10 border-b border-gray-200">
                                <h4 className="font-semibold text-sm text-teal-600 flex items-center gap-2">
                                  <CheckCircle className="h-4 w-4" />
                                  Urgent & High Priority Tasks ({urgentTodos.length})
                                </h4>
                              </div>
                              <div className="max-h-64 overflow-y-auto">
                                {urgentTodos.map((todo) => (
                                  <Link
                                    key={todo.id}
                                    to="/todos"
                                    onClick={() => setNotificationsOpen(false)}
                                    className="block px-4 py-3 hover:bg-gray-50 border-b border-gray-100 transition-colors"
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                                        todo.priority === 'urgent' 
                                          ? 'bg-red-100 text-red-600' 
                                          : 'bg-orange-100 text-orange-600'
                                      }`}>
                                        {todo.priority === 'urgent' ? (
                                          <Zap className="h-3.5 w-3.5" />
                                        ) : (
                                          <Target className="h-3.5 w-3.5" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm text-gray-900 truncate">{todo.title}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          {todo.assignee_first_name} {todo.assignee_last_name}
                                          {todo.due_date && (
                                            <> • Due: {formatDateLocale(todo.due_date)}</>
                                          )}
                                        </p>
                                        {todo.status === 'in_progress' && (
                                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-teal-100 text-teal-800 rounded-full">
                                            In Progress
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Today's Recaps */}
                          {todayRecaps.length > 0 && (
                            <div>
                              <div className="px-4 py-2 bg-teal-500/10 border-b border-gray-200">
                                <h4 className="font-semibold text-sm text-teal-600 flex items-center gap-2">
                                  <Activity className="h-4 w-4" />
                                  Today's Daily Recaps ({todayRecaps.length})
                                </h4>
                              </div>
                              <div className="max-h-64 overflow-y-auto">
                                {todayRecaps.map((recap) => (
                                  <Link
                                    key={recap.id}
                                    to="/recaps"
                                    onClick={() => setNotificationsOpen(false)}
                                    className="block px-4 py-3 hover:bg-gray-50 border-b border-gray-100 transition-colors"
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className="p-1.5 rounded-lg bg-teal-100 text-teal-600 flex-shrink-0">
                                        <Activity className="h-3.5 w-3.5" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm text-gray-900">
                                          {recap.first_name} {recap.last_name}
                                          {recap.store_name && ` • ${recap.store_name}`}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          {recap.problems_to_solve && `Issues: ${recap.problems_to_solve.substring(0, 50)}...`}
                                          {!recap.problems_to_solve && recap.daily_remarks && `Remarks: ${recap.daily_remarks.substring(0, 50)}...`}
                                        </p>
                                        {recap.is_validated ? (
                                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                            Validated
                                          </span>
                                        ) : (
                                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                                            Pending Validation
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Empty State */}
                          {urgentTodos.length === 0 && todayRecaps.length === 0 && reminderTodos.length === 0 && (
                            <div className="p-8 text-center">
                              <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                              <p className="text-sm font-medium text-gray-600">No notifications</p>
                              <p className="text-xs text-gray-500 mt-1">All caught up!</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Notifications (non-admin - hidden button) */}
              {userRole !== 'admin' && (
                <button 
                  className={`p-1.5 sm:p-2 md:p-2.5 lg:p-3 rounded-lg sm:rounded-xl md:rounded-2xl transition-all duration-300 hover:scale-110 relative flex-shrink-0 opacity-50 cursor-not-allowed ${
                    isDarkMode 
                      ? 'text-gray-600' 
                      : 'text-gray-300'
                  }`}
                  aria-label="Notifications"
                  disabled
                >
                  <Bell className="h-4 w-4 sm:h-4.5 sm:w-4.5 md:h-5 md:w-5 lg:h-6 lg:w-6" />
                </button>
              )}

              {/* Language Switcher - Smaller on mobile */}
              <div className="flex-shrink-0">
                <LanguageSwitcher />
              </div>

              {/* User menu */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className={`flex items-center space-x-1 md:space-x-2 lg:space-x-3 p-1 sm:p-1.5 md:p-2 lg:p-2.5 xl:p-3 rounded-lg sm:rounded-xl md:rounded-2xl transition-all duration-300 hover:scale-105 ${
                    isDarkMode 
                      ? 'text-gray-300 hover:bg-gray-700/50' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  aria-label="User menu"
                >
                  <div className="h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9 lg:h-10 lg:w-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                    <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-4 md:w-4 lg:h-5 lg:w-5 text-white" />
                  </div>
                  <div className="hidden xl:block text-left">
                    <span className="text-xs lg:text-sm font-bold">
                      {user?.first_name} {user?.last_name}
                    </span>
                    <p className="text-[9px] lg:text-[10px] xl:text-xs text-gray-500 capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown className="h-3 w-3 md:h-3.5 md:w-3.5 lg:h-4 lg:w-4 hidden lg:block" />
                </button>

                {/* User dropdown */}
                {userMenuOpen && (
                  <div 
                    className={`absolute right-0 top-full mt-2 w-48 sm:w-56 rounded-xl sm:rounded-2xl shadow-2xl py-1 sm:py-2 ${
                      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    } border`}
                    style={{ 
                      zIndex: 99999,
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '8px'
                    }}
                  >
                    {user?.role?.toLowerCase() === 'admin' && (
                      <button
                        className={`w-full text-left flex items-center px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium transition-all duration-200 hover:bg-gray-100 ${
                          isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setUserMenuOpen(false);
                          navigate('/profile');
                        }}
                      >
                        <div className="p-1.5 sm:p-2 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-lg sm:rounded-xl mr-2 sm:mr-3 flex-shrink-0">
                          <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                        </div>
                        {t('navigation.profile')}
                      </button>
                    )}
                    {user?.role?.toLowerCase() === 'admin' && (
                      <button
                        className={`w-full text-left flex items-center px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium transition-all duration-200 hover:bg-gray-100 ${
                          isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setUserMenuOpen(false);
                          navigate('/settings');
                        }}
                      >
                        <div className="p-1.5 sm:p-2 bg-gradient-to-r from-green-500 to-teal-600 rounded-lg sm:rounded-xl mr-2 sm:mr-3 flex-shrink-0">
                          <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                        </div>
                        {t('navigation.settings')}
                      </button>
                    )}
                    <hr className={`my-1 sm:my-2 mx-3 sm:mx-4 ${
                      isDarkMode ? 'border-gray-700' : 'border-gray-200'
                    }`} />
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setUserMenuOpen(false);
                        logout();
                      }}
                      className={`w-full text-left flex items-center px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-medium transition-all duration-200 hover:bg-red-50 ${
                        isDarkMode ? 'text-gray-300 hover:bg-red-500/20' : 'text-red-600'
                      }`}
                    >
                      <div className="p-1.5 sm:p-2 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-lg sm:rounded-xl mr-2 sm:mr-3 flex-shrink-0">
                        <LogOut className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                      </div>
                      {t('navigation.signOut')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Spacer to push content below fixed header */}
        <div className="h-14 sm:h-16 md:h-20 flex-shrink-0"></div>

        {/* Page content */}
        <main className={`relative p-3 sm:p-4 md:p-5 lg:p-6 xl:p-8 overflow-x-auto transition-all duration-500 ease-in-out ${
          isDarkMode ? 'bg-transparent' : 'bg-transparent'
        }`} style={{ zIndex: 10 }}>
          {children}
        </main>
      </div>

      {/* Reminder Notification Card - Compact Modern Design */}
      {showReminderCard && reminderTodos.length > 0 && currentReminderIndex < reminderTodos.length && (
        <div className="fixed right-3 sm:right-4 top-16 sm:top-[4.25rem] md:top-24 z-50 w-[calc(100vw-1.5rem)] sm:w-80 md:w-96" style={{ zIndex: 999999 }}>
          <div className="relative bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden backdrop-blur-sm">
            {/* Compact Header */}
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-teal-50 to-emerald-50 border-b border-gray-100">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="p-1.5 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex-shrink-0">
                  <Bell className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-sm text-gray-900 truncate">Reminder</h3>
                  <p className="text-xs text-gray-500 truncate">
                    {currentReminderIndex + 1} of {reminderTodos.length}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  const nextIndex = currentReminderIndex + 1;
                  if (nextIndex >= reminderTodos.length) {
                    setShowReminderCard(false);
                    setCurrentReminderIndex(0);
                  } else {
                    setCurrentReminderIndex(nextIndex);
                  }
                }}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white/80 rounded-lg transition-all flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Compact Content */}
            {reminderTodos[currentReminderIndex] && (
              <div className="p-3 space-y-2">
                {/* Priority & Title */}
                <div className="flex items-start gap-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                    reminderTodos[currentReminderIndex].priority === 'urgent' 
                      ? 'bg-red-100 text-red-700' 
                      : reminderTodos[currentReminderIndex].priority === 'high'
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-teal-100 text-teal-700'
                  }`}>
                    {reminderTodos[currentReminderIndex].priority.toUpperCase()}
                  </span>
                  <h4 className="font-semibold text-sm text-gray-900 flex-1 line-clamp-2">
                    {reminderTodos[currentReminderIndex].title}
                  </h4>
                </div>
                
                {/* Description */}
                {reminderTodos[currentReminderIndex].description && (
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {reminderTodos[currentReminderIndex].description}
                  </p>
                )}
                
                {/* Assigned To */}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <User className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">
                    {reminderTodos[currentReminderIndex].assignee_first_name} {reminderTodos[currentReminderIndex].assignee_last_name}
                  </span>
                </div>
                
                {/* Action Button */}
                <button
                  onClick={() => openReminderTaskModal(reminderTodos[currentReminderIndex])}
                  className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white px-3 py-2 rounded-lg font-medium transition-all text-xs flex items-center justify-center gap-2"
                >
                  <ListTodo className="h-3.5 w-3.5" />
                  <span>View Task</span>
                </button>

                {/* Progress Dots */}
                {reminderTodos.length > 1 && (
                  <div className="flex items-center justify-center gap-1.5 pt-1">
                    {reminderTodos.map((_: Todo, index: number) => (
                      <div
                        key={index}
                        className={`rounded-full transition-all ${
                          index === currentReminderIndex
                            ? 'w-6 h-1.5 bg-gradient-to-r from-teal-500 to-emerald-500'
                            : 'w-1.5 h-1.5 bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <TodoDetailsModal
        isOpen={showReminderDetailsModal}
        onClose={() => {
          setShowReminderDetailsModal(false);
          setSelectedReminderTodo(null);
          fetchNotifications();
        }}
        todo={selectedReminderTodo}
        onStatusChange={handleReminderStatusChange}
        onAddComment={handleReminderAddComment}
      />

      {/* Scroll to Top/Bottom Button */}
      <ScrollToTopBottom />

      {/* Floating chatbot button - opens assistant in a panel */}
      {user && <FloatingChatButton />}
    </div>
  );
};

export default Layout;
